import i18next, { TFunction } from 'i18next';
import { EmhConfig, Environment, SearchResultsApi } from '@dh-io-emh/search-result-store';
import { getAemEnvironmentVariables, getConfig, getTranslations } from '@dh-io-emh/config-store';

export type AemStage = 'dev' | 'test' | 'int' | 'pprod' | 'preprod' | 'prod';
export type PlsEnvironment = 'dev' | 'int' | 'preprod' | 'prod';

// TODO(CARSBR-2346): Move this mapping to upcoming runtime config
const aemStageToEmhEnvironment = (aemStage: AemStage): Environment => {
  switch (aemStage) {
    case 'dev':
      return 'dev-01';
    case 'test':
      return 'dev-01';
    case 'int':
      return 'int';
    case 'pprod':
      return 'preprod';
    case 'preprod':
      return 'preprod';
    case 'prod':
      return 'prod';
  }
};

export const aemStageToPlsEnvironment = (aemStage: AemStage): PlsEnvironment => {
  switch (aemStage) {
    case 'dev':
      return 'dev';
    case 'test':
      return 'dev';
    case 'int':
      return 'int';
    case 'pprod':
      return 'preprod';
    case 'preprod':
      return 'preprod';
    case 'prod':
      return 'prod';
  }
};

interface EmhContext {
  // lower case two letter ISO code
  language: string;
  // lower case two letter ISO code
  country: string;
  locale: string;
  aemStage: AemStage;
  emhEnvironment: Environment;
  searchApi: SearchResultsApi;
  emhConfig: EmhConfig;
  t: TFunction;
}

let initPromise: Promise<EmhContext>;

/**
 * Initializes the search API and i18next. Returns a promise that resolves once
 * all required services are initialized. Prevents multiple initializations.
 */
export const initEmhContext = (): Promise<EmhContext> => {
  if (!initPromise) {
    initPromise = (async (): Promise<EmhContext> => {
      const aemEnv = await getAemEnvironmentVariables();
      const language = aemEnv.language;
      const country = aemEnv.country.toLowerCase();
      const locale = `${language}-${country}`;
      const aemStage = aemEnv.stage.toLowerCase() as AemStage;
      const emhEnvironment = aemStageToEmhEnvironment(aemStage);
      const emhConfig = (await getConfig()) as EmhConfig;
      const translation = await getTranslations();
      const searchApi = new SearchResultsApi(
        emhConfig.apiBaseUrl,
        emhConfig.siteId,
        emhConfig.assortmentBaseCategoryMapping.vehicle,
        language,
        country,
      );
      const t = await i18next.init({
        lng: language,
        resources: {
          [language]: {
            translation,
          },
        },
      });

      return { language, country, locale, aemStage, emhEnvironment, searchApi, emhConfig, t };
    })();
  }

  return initPromise;
};
